<script>
import Layout from "@/router/layouts/main";
import { api } from "@/state/services";

export default {
  locales: {
    pt: {
    },
    es: {
    }
  },
  components: {
    Layout,
  },
  data() {
    return {
      list: null,
      page: 1,
      per: 20,
      total: 0,
    };
  },
  computed: {
    totalPages() {
      if (!this.total || !this.per) {
        return 1;
      }
      return Math.max(1, Math.ceil(this.total / this.per));
    },
    canPrev() {
      return this.page > 1;
    },
    canNext() {
      return this.page < this.totalPages;
    },
  },
  methods: {
    getEbooks() {
      this.list = null;
      const params = {
        page: this.page,
        per: this.per,
      };
      api.get("ebooks", { params }).then((response) => {
        if (response.data.status === "success") {
          this.list = response.data.list || [];
          this.total = typeof response.data.total === "number" ? response.data.total : 0;
        } else {
          this.list = [];
          this.total = 0;
        }
      }).catch(() => {
        this.list = [];
        this.total = 0;
      });
    },
    goPrev() {
      if (!this.canPrev) return;
      this.page -= 1;
      this.getEbooks();
    },
    goNext() {
      if (!this.canNext) return;
      this.page += 1;
      this.getEbooks();
    },
  },
  mounted() {
    this.getEbooks();
  },
};
</script>

<template>
  <Layout>
    <ol class="breadcrumb">
      <li class="breadcrumb-item">
        <router-link tag="a" to="/">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" style="fill: rgba(0, 0, 0, 1);">
            <path d="M5 22h14a2 2 0 0 0 2-2v-9a1 1 0 0 0-.29-.71l-8-8a1 1 0 0 0-1.41 0l-8 8A1 1 0 0 0 3 11v9a2 2 0 0 0 2 2zm5-2v-5h4v5zm-5-8.59 7-7 7 7V20h-3v-5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v5H5z"></path>
          </svg>
        </router-link>
      </li>
      <li class="breadcrumb-item">E-books</li>
      <li class="breadcrumb-item">Catálogo</li>
    </ol>

    <div class="page-title-box d-md-flex justify-content-between">
      <div>
        <h3>Catálogo</h3>
      </div>
    </div>

    <div v-if="list === null" class="card">
      <div class="card-body min-vh-25 d-flex justify-content-center align-items-center">
        <b-spinner variant="default" role="status"></b-spinner>
      </div>
    </div>

    <div v-else-if="list.length === 0" class="card">
      <div class="card-body text-center min-vh-25 d-flex align-items-center justify-content-center">
        Nenhum e-book disponível no momento.
      </div>
    </div>

    <div v-else class="catalog-ebooks">
      <div class="row">
        <div class="col-6 col-md-4 col-lg-3 mb-4" v-for="row in list" :key="row.id">
          <router-link tag="a" :to="'/ebooks/' + row.id" class="ebook-card-link">
            <div class="card ebook-card h-100">
              <div class="ebook-cover-wrap">
                <img
                  v-if="row.cover_url"
                  class="ebook-cover"
                  :src="row.cover_url"
                  :alt="row.title"
                  loading="lazy"
                />
                <div v-else class="ebook-cover-placeholder" aria-hidden="true">
                  <span class="ebook-cover-placeholder-text">Sem capa</span>
                </div>
              </div>
              <div class="ebook-card-body">
                <h5 class="ebook-card-title">{{ row.title }}</h5>
                <p class="ebook-card-price">{{ row.price | currency }}</p>
              </div>
            </div>
          </router-link>
        </div>
      </div>

      <div
        v-if="totalPages > 1"
        class="card ebook-pagination-card"
      >
        <div class="card-body d-flex flex-wrap align-items-center justify-content-between py-3">
          <span class="small text-muted mb-2 mb-sm-0">
            Página {{ page }} de {{ totalPages }} ({{ total }} títulos)
          </span>
          <div>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary mr-2"
              :disabled="!canPrev"
              @click="goPrev"
            >
              Anterior
            </button>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              :disabled="!canNext"
              @click="goNext"
            >
              Próxima
            </button>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
/* Alinhado ao tema da ficha do e-book (view.vue), sem mudar o layout global (breadcrumb + cards) */
.catalog-ebooks {
  --ebook-ink: #1a1512;
  --ebook-muted: #6b6560;
  --ebook-default-tint: #f5f2f8;
  --ebook-default-tint-deep: #ebe4f2;
  --ebook-accent: #452058;
  --ebook-accent-soft: rgba(69, 32, 88, 0.08);
  --ebook-radius: 10px;
  --ebook-card-border: 1px solid rgba(0, 0, 0, 0.125);
}

.ebook-card-link {
  text-decoration: none;
  color: inherit;
  display: block;
  height: 100%;
}

.ebook-card {
  border: var(--ebook-card-border);
  border-radius: var(--ebook-radius);
  overflow: hidden;
  box-shadow: none;
  background: #fff;
  transition: transform 0.2s ease-out, box-shadow 0.2s ease-out;
  cursor: pointer;
}

@media (prefers-reduced-motion: reduce) {
  .ebook-card {
    transition: none;
  }
}

.ebook-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 24px rgba(26, 21, 18, 0.1);
}

.ebook-card:focus-within {
  outline: 2px solid var(--ebook-accent);
  outline-offset: 2px;
}

/* Capa um pouco mais alta e uniforme no grid (proporção livro) */
.ebook-cover-wrap {
  position: relative;
  width: 100%;
  aspect-ratio: 2 / 3;
  min-height: 200px;
  background: var(--ebook-default-tint);
  overflow: hidden;
}

@media (min-width: 768px) {
  .ebook-cover-wrap {
    min-height: 240px;
  }
}

@media (min-width: 992px) {
  .ebook-cover-wrap {
    min-height: 260px;
  }
}

.ebook-cover {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.ebook-cover-placeholder {
  width: 100%;
  height: 100%;
  min-height: inherit;
  background: var(--ebook-default-tint-deep);
  border-bottom: 1px solid rgba(69, 32, 88, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}

.ebook-cover-placeholder-text {
  font-size: 0.85rem;
  color: var(--ebook-muted);
}

.ebook-card-body {
  padding: 1rem 1rem 1.15rem;
  background: linear-gradient(180deg, #fff 0%, var(--ebook-accent-soft) 100%);
  border-top: 1px solid rgba(69, 32, 88, 0.06);
}

.ebook-card-title {
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.35;
  color: var(--ebook-ink);
  margin: 0 0 0.5rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

@media (min-width: 768px) {
  .ebook-card-title {
    font-size: 1rem;
  }
}

.ebook-card-price {
  font-family: "Mulish", "Poppins", sans-serif;
  font-size: 1.15rem;
  font-weight: 800;
  color: var(--ebook-accent);
  letter-spacing: -0.02em;
  margin: 0;
}

.ebook-pagination-card {
  margin-top: 0.5rem;
}
</style>
